import { IconArrowUndone } from '@consta/icons/IconArrowUndone'
import { Button } from '@consta/uikit/Button'
import { FieldGroup } from '@consta/uikit/FieldGroup'
import { clamp } from '@library/utils'
import { isDefined } from 'common/helpers'
import type { Nullable } from 'common/types'
import type { FC, KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

import { NumericInput } from '../NumericInput'
import css from './AttributePanel.css'
import { getStep, specificRound } from './helpers'

type AttributePanelProps = {
  label: string
  value: number | undefined
  disabled: boolean
  updateValue: (value: number) => void
  resetValue: () => void
  minValue?: number
  maxValue?: number
}

const MIN_AMPLITUDE = 0.0001
const MAX_AMPLITUDE = 9999999.9999

export const AttributePanel: FC<AttributePanelProps> = ({
  label,
  value,
  disabled,
  updateValue,
  resetValue,
  maxValue = MAX_AMPLITUDE,
  minValue = MIN_AMPLITUDE,
}) => {
  const inputRef = useRef<Nullable<HTMLInputElement>>(null)
  const [inputValue, setInputValue] = useState<string>('')

  useEffect(() => {
    if (isDefined(value)) {
      const roundedValue = specificRound(value, 4)
      setInputValue(roundedValue.toString())
    }
  }, [value])

  const handleChange = (value: Nullable<string>) => {
    setInputValue(value ?? '')
  }

  const handleUpdate = () => {
    const value = +(inputRef.current?.value ?? maxValue)
    const limitedValue = clamp(value, minValue, maxValue)
    const roundedValue = specificRound(limitedValue, 4)
    updateValue(roundedValue)
    setInputValue(roundedValue.toString())
  }

  const handleEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      inputRef.current?.blur()
    }
  }

  const step = getStep(inputValue)

  return (
    <FieldGroup size="xs">
      <NumericInput
        inputRef={inputRef}
        className={css.numericInput}
        size="xs"
        label={label}
        labelPosition="left"
        step={step}
        value={inputValue}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleUpdate}
        onKeyDown={handleEnter}
      />
      <Button
        view="ghost"
        iconLeft={IconArrowUndone}
        onlyIcon={true}
        disabled={false}
        onClick={resetValue}
      />
    </FieldGroup>
  )
}
